import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { TokenService } from '@core/services/token.service';
import { ApiRole } from '@core/models/api/user.model';
import { toFrontendRole } from '@core/mappers/admin.mappers';

interface DecodedAccessToken {
  sub: string;
  email: string;
  role: ApiRole;
  exp?: number;
}

/**
 * Decodes the JWT payload without verifying the signature (verification happens
 * server-side on every request) — only used here to read the role claim for
 * client-side route gating, and to check expiry so we don't treat a stale
 * token as a valid session.
 */
function decodeAccessToken(token: string): DecodedAccessToken | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized)) as DecodedAccessToken;
    return decoded;
  } catch {
    return null;
  }
}

function isExpired(decoded: DecodedAccessToken): boolean {
  if (!decoded.exp) return false;
  return decoded.exp * 1000 <= Date.now();
}

/**
 * Guards `/admin/*` and `/customer/*` routes. Requires a (non-expired) stored
 * access token, and — when the route declares `data: { roles: [...] }` —
 * requires the token's role claim to be one of the allowed roles. Redirects
 * to the landing page otherwise.
 */
export const authGuard: CanActivateFn = (route) => {
  const tokenService = inject(TokenService);
  const router = inject(Router);

  const accessToken = tokenService.getAccessToken();
  if (!accessToken) {
    return router.createUrlTree(['/']);
  }

  const decoded = decodeAccessToken(accessToken);
  if (!decoded || isExpired(decoded)) {
    tokenService.clear();
    return router.createUrlTree(['/']);
  }

  const allowedRoles = route.data?.['roles'] as ('Admin' | 'Manager' | 'Customer')[] | undefined;
  if (allowedRoles && allowedRoles.length > 0) {
    const currentRole = toFrontendRole(decoded.role);
    if (!allowedRoles.includes(currentRole)) {
      return router.createUrlTree(['/']);
    }
  }

  return true;
};
